import { TextField } from "@mui/material";
import { Controller } from "react-hook-form";

import { SKILL_DESCRIPTION_MAX_LENGTH } from "@/shared/domain/skills";

import styles from "./SkillVersionFields.module.css";
import type { SkillVersionForm } from "./skillVersionForm";
import { TopicBodyEditor } from "./TopicBodyEditor";

interface Props {
  form: SkillVersionForm;
  orgId: string | undefined;
}

/**
 * The two editable fields of a skill version — "when to use" and the Markdown
 * procedure — plus the save error, if the last attempt failed.
 *
 * Presentational: field state, validation, and save handling live in
 * {@link SkillVersionForm}, so every surface that edits a version renders
 * identical fields over identical semantics.
 *
 * Both fields go through `Controller` rather than `register`: the body editor
 * is a controlled component with its own `value` / `onChange` contract, and
 * MUI's `TextField` forwards a bare `ref` to its wrapper rather than to the
 * input. Binding them the same way keeps the two from needing different rules.
 */
export function SkillVersionFields({ form, orgId }: Props) {
  return (
    <>
      <Controller
        name="description"
        control={form.control}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            label="When to use"
            error={fieldState.error !== undefined}
            helperText={
              fieldState.error?.message ??
              `Short description the AI uses to decide when to apply this skill (≤${SKILL_DESCRIPTION_MAX_LENGTH} chars).`
            }
            multiline
            minRows={2}
            inputProps={{ maxLength: SKILL_DESCRIPTION_MAX_LENGTH }}
            required
            fullWidth
          />
        )}
      />
      <Controller
        name="body"
        control={form.control}
        render={({ field, fieldState }) => (
          <TopicBodyEditor
            label="Procedure / body"
            value={field.value}
            onChange={field.onChange}
            onBlur={field.onBlur}
            topicNames={form.topicNames}
            orgId={orgId}
            error={fieldState.error !== undefined}
            helperText={
              fieldState.error?.message ??
              "Markdown supported. Type “[[” to reference a topic — referenced topics are pre-loaded for the AI."
            }
            inputClassName={styles.bodyInput}
            grow
            required
          />
        )}
      />
      {form.errorMessage && (
        <small className={styles.errorMessage}>{form.errorMessage}</small>
      )}
    </>
  );
}
